import { ReactNode, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  requestInventorySchemaClear,
  retrieveInventorySchema,
} from '@atom/actions/inventorySchemaActions';
import { Button, Icon, IconButton, Modal, Progress } from '@atom/mui';
import {
  filterSchemaByMarkerId,
  inventorySchema,
} from '@atom/selectors/schemaSelectors';
import colors from '@atom/styles/colors';
import { RootState } from '@atom/types/store';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import FhwaNoElementsDialog from './FhwaNoElementsDialog';
import FhwaSchemaElementGroupSection from './FhwaSchemaElementGroupSection';
import FhwaSchemaElementListRow from './FhwaSchemaElementListRow';

import classes from './elements.module.css';

const styles = {
  dialog: {
    bodyStyle: { margin: 0, padding: 0 },
  },
  actionButtons: {
    cancel: { marginRight: '1rem' },
  },
  headerIcon: { height: '24px', padding: 0 },
};

interface Props {
  schemaId?: string;
  loadingBatchCreation: boolean;
  title: string;
  assetName: string;
  assetId: string;
  onBatchCreateAction: () => any;
  isFhwaForm: boolean;
  existingSchemaIds: { [schemaId: string]: any };
}

const FhwaAddElementModal = (props: Props) => {
  const {
    title,
    assetName,
    assetId,
    schemaId,
    onBatchCreateAction,
    existingSchemaIds,
  } = props;
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set([]));
  const [schemaQuantities, setSchemaQuantities] = useState({});

  const loading = useSelector(
    (state: RootState) => state.loading.loadingInventorySchema,
  );

  const loadingBatchCreation = useSelector(
    (state: RootState) => state.loading.loadingElementBatchCreation,
  );

  const loadingSchemaTreeNode = useSelector(
    (state: RootState) => state.loading.loadingSchemaTreeNode,
  );

  const schemaSlice = useSelector(inventorySchema);
  const schema: any = useMemo(
    () => filterSchemaByMarkerId(schemaSlice, props),
    [schemaSlice, props.isFhwaForm],
  );

  const setInitialState = () => {
    setOpen(false);
    setSelectedItems(new Set([]));
    setSchemaQuantities({});
  };

  const closeModal = () => {
    if (!R.isEmpty(schema)) {
      dispatch(requestInventorySchemaClear());
    }
    setInitialState();
  };

  const openModal = () => {
    dispatch(retrieveInventorySchema({ id: schemaId }));
    setOpen(true);
  };

  const confirm = () => {
    const elements = [...selectedItems].reduce((previous, next) => {
      const pathAndId = next.split('.');
      const newSchemaId = R.last(pathAndId);
      const path = R.dropLast(1, pathAndId);

      const batchItem = {
        schemaId: newSchemaId,
        quantity: schemaQuantities[next] || 1,
      };

      if (R.isEmpty(path)) {
        return [...previous, batchItem];
      }

      const indexPath = path.reduce((previousPath, nextPathId) => {
        // @ts-ignore
        const lens = R.lensPath(previousPath);
        const section = R.view(lens, previous);

        // @ts-ignore
        const nextIndex = section.findIndex(
          item => item.schemaId === nextPathId,
        );
        // @ts-ignore
        return [...previousPath, nextIndex, 'elements'];
      }, []);
      // @ts-ignore
      const lens = R.lensPath(indexPath);

      const view = R.view(lens, previous) || [];
      // @ts-ignore
      return R.set(lens, [...view, batchItem], previous);
    }, []);

    const batchSchemaRequestBody = {
      assetId,
      assetSchemaId: schema.id,
      elements,
    };
    // @ts-ignore
    onBatchCreateAction(batchSchemaRequestBody);
    return closeModal();
  };

  const toggleChecked = (id: string) =>
    setSelectedItems(toggleFromSet(selectedItems, id));

  const renderElements = (): ReactNode => {
    if (R.isEmpty(schema) || R.isEmpty(schema.elementGroups)) {
      return <div />;
    }

    const elementGroups = schema.elementGroups;

    return elementGroups.map((elementGroup, elementGroupIndex) => {
      const elementExistsOnAsset = existingSchemaIds
        ? !!existingSchemaIds[elementGroup.id]
        : false;

      if (elementGroup.isGroup) {
        return (
          <FhwaSchemaElementGroupSection
            key={elementGroup.name}
            elementGroupIndex={elementGroupIndex}
            loadingSchemaTreeNode={loadingSchemaTreeNode}
            onClick={toggleChecked}
            elementGroup={elementGroup}
            selectedItems={selectedItems}
            schemaQuantities={schemaQuantities}
            elementExistsOnAsset={elementExistsOnAsset}
          />
        );
      }

      const element = elementGroup.elements[0];

      return (
        <div className={classes.elementListRowContainer} key={element.id}>
          <FhwaSchemaElementListRow
            isChecked={selectedItems.has(element.id)}
            element={element}
            loading={loadingSchemaTreeNode}
            onClick={toggleChecked}
            value={schemaQuantities[element.id]}
            selectedItems={selectedItems}
            schemaQuantities={schemaQuantities}
            elementPath={['elementGroups', elementGroupIndex, 'elements', 0]}
            elementExistsOnAsset={elementExistsOnAsset}
          />
        </div>
      );
    });
  };

  const addModalDisabledState = !selectedItems.size;

  const getFooter = () => {
    return (
      <>
        <Button sx={styles.actionButtons.cancel} onClick={closeModal}>
          Cancel
        </Button>
        <Button
          disabled={addModalDisabledState}
          onClick={confirm}
          color="primary"
          variant="contained"
        >
          Add
        </Button>
      </>
    );
  };

  const dialogContent =
    loading || loadingBatchCreation || R.isEmpty(schema) ? (
      <div className={classes.elementModalSpinnerContainer}>
        <Progress />
      </div>
    ) : (
      <div>
        <div className={classes.elementModalSubheading}>
          Select elements and quantities from inventory type for
          <span className={classes.elementModalAssetName}>{assetName}</span>
        </div>
        <div className={classes.elementModalSelectionBackdrop}>
          {renderElements()}
        </div>
      </div>
    );

  const dialog = R.isEmpty(schema.elementGroups) ? (
    <FhwaNoElementsDialog
      title="No elements available."
      assetName={assetName}
      closeModal={closeModal}
      open={open}
    />
  ) : (
    <Modal
      title={title}
      footer={getFooter()}
      width="lg"
      style={styles.dialog.bodyStyle}
      open={open}
    >
      {dialogContent}
    </Modal>
  );

  return (
    <div>
      <div className={classes.headerIcon}>
        <IconButton onClick={openModal} style={styles.headerIcon}>
          <Icon color={colors.neutral.gray} style={{ padding: 0 }}>
            add
          </Icon>
        </IconButton>
      </div>
      {dialog}
    </div>
  );
};

export default FhwaAddElementModal;
